import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js';

console.log('Three.js version:', THREE.REVISION);

// Path to your model
export let modelPath = '/wp-content/themes/hiroshimax/assets/objects/triangle.glb';
export let moviePath = '/wp-content/themes/hiroshimax/assets/movie/kv1.mp4';

function loadVideoAsTexture(path: string): Promise<{texture: THREE.VideoTexture; aspect: number} | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.src = path;
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.style.display = 'none';
    document.body.appendChild(video);

    video.addEventListener('loadeddata', () => {
      video.play().catch((e) => console.warn('Video play() failed', e));
      const texture = new THREE.VideoTexture(video);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.format = THREE.RGBAFormat;

      // Repeat the texture
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(1.6, 1.5); // Repeat 2 times in X and Y

      // Shift the texture in X
      // texture.offset.x = -0.4;

      resolve({texture, aspect: video.videoWidth / video.videoHeight});
    });

    video.onerror = () => {
      console.error(`Failed to load video at ${path}`);
      resolve(null);
    };
  });
}

export const initThreeResults = (container: HTMLElement): void => {
  // Scene
  const scene = new THREE.Scene();

  // Store references to left and right objects globally in this function
  let leftObject: THREE.Object3D | null = null;
  let rightObject: THREE.Object3D | null = null;

  // Camera
  const fov = (45 * Math.PI) / 180; // radians
  const aspect = container.offsetWidth / container.offsetHeight;

  // Choose how much of the scene you want visible at start/end (in scene units)
  const VISIBLE_WIDTH_START = 8; // e.g., fits both triangles offscreen
  const VISIBLE_WIDTH_END = window.innerWidth <= 700 ? 3 : 5; // e.g., zooms in

  const CAMERA_Z_START = VISIBLE_WIDTH_START / (2 * Math.tan(fov / 2) * aspect);
  const CAMERA_Z_END = VISIBLE_WIDTH_END / (2 * Math.tan(fov / 2) * aspect);
  const CAMERA_SMOOTH = 0.08; // Exponential smoothing factor
  let targetCameraZ = CAMERA_Z_START;
  let currentCameraZ = CAMERA_Z_START;
  const camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
  camera.position.set(0, 0, CAMERA_Z_START);

  // Renderer
  const renderer = new THREE.WebGLRenderer({alpha: true});
  renderer.setClearColor(0xcccccc, 0); // 0 alpha = fully transparent
  renderer.setSize(container.offsetWidth, container.offsetHeight);
  renderer.toneMappingExposure = 1.2; // default is 1.0, try higher values
  container.appendChild(renderer.domElement);

  // Add a light
  const light = new THREE.DirectionalLight(0xffffff, 2);
  light.position.set(-0.2, 2, 1);
  scene.add(light);

  // Add ambient light to brighten the dark side
  const ambient = new THREE.AmbientLight(0xffffff, 1.01);
  scene.add(ambient);

  let mixerLeft: THREE.AnimationMixer | null = null;
  let mixerRight: THREE.AnimationMixer | null = null;

  // --- Animation speed logic ---
  const MAX_SPEED = 10; // Maximum absolute animation speed (adjustable)
  let lastScrollY = window.scrollY;
  let lastTimestamp = performance.now();
  let isScrolling = false;
  let scrollEndTimeout: number | null = null;
  let currentSpeed = 1; // 1 = normal forward, -1 = normal rewind
  let targetSpeed = 1;
  const SPEED_DECAY = 0.02; // Exponential decay rate for speed changes

  // Track recent scroll events for average speed
  type ScrollEvent = {delta: number; time: number};
  const scrollWindow = 150; // ms
  let scrollEvents: ScrollEvent[] = [];

  function updateScrollSpeed() {
    const now = performance.now();
    const newScrollY = window.scrollY;
    const deltaY = newScrollY - lastScrollY;
    const deltaTime = (now - lastTimestamp) / 1000; // seconds
    lastScrollY = newScrollY;
    lastTimestamp = now;
    isScrolling = true;
    if (scrollEndTimeout !== null) {
      clearTimeout(scrollEndTimeout);
    }
    // Add this scroll event to the queue
    scrollEvents.push({delta: deltaY, time: now});
    // Remove old events
    scrollEvents = scrollEvents.filter((e) => now - e.time <= scrollWindow);
    // Calculate total delta and average speed
    const totalDelta = scrollEvents.reduce((sum, e) => sum + e.delta, 0);
    const windowSec = scrollWindow / 1000;
    const avgSpeed = totalDelta / windowSec / 1000; // px/sec scaled
    // Set currentSpeed based on average speed
    currentSpeed = 1 + avgSpeed * 2; // Sensitivity factor
    // Clamp for sanity
    if (currentSpeed > MAX_SPEED) currentSpeed = MAX_SPEED;
    if (currentSpeed < -MAX_SPEED) currentSpeed = -MAX_SPEED;
    scrollEndTimeout = window.setTimeout(() => {
      isScrolling = false;
      // On scroll end, set target speed based on currentSpeed
      if (currentSpeed > 0) {
        targetSpeed = 1;
      } else {
        targetSpeed = -1;
      }
    }, 120); // 120ms after last scroll event, consider scroll ended
  }
  window.addEventListener('scroll', updateScrollSpeed, {passive: true});

  // --- Load video texture and apply to triangle model ---
  const textureLoader = new THREE.TextureLoader();
  const leftTexture = textureLoader.load('/wp-content/themes/hiroshimax/assets/movie/pink.png');
  const rightTexture = textureLoader.load('/wp-content/themes/hiroshimax/assets/movie/blue.png');

  const loader = new GLTFLoader();
  loader.load(
    modelPath,
    (gltf) => {
      gltf.scene.traverse((child) => {
        if (child.type === 'Mesh') {
          console.log('Found mesh:', child);
          const mesh = child as THREE.Mesh;
          console.log('UVs:', mesh.geometry.attributes.uv);
          // Replace material to ensure it supports textures
          mesh.material = new THREE.MeshStandardMaterial({
            map: leftTexture,
            emissive: new THREE.Color(0xffffff),
            emissiveMap: leftTexture,
            emissiveIntensity: 0.5,
            side: THREE.DoubleSide,
            metalness: 0.2,
            roughness: 0.5,
          });
          mesh.material.needsUpdate = true;

          if (!mesh.geometry.attributes.uv) {
            const geometry = mesh.geometry;
            const pos = geometry.attributes.position as THREE.BufferAttribute;
            const uvs: number[] = [];
            for (let i = 0; i < pos.count; i++) {
              // Simple planar mapping (X/Y to U/V)
              uvs.push(((pos.getX ? pos.getX(i) : 0) + 1) / 2, ((pos.getY ? pos.getY(i) : 0) + 1) / 2);
            }
            geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
            geometry.attributes.uv.needsUpdate = true;
          }
        }
      });
      // --- LEFT OBJECT ---
      leftObject = gltf.scene;
      leftObject.position.set(-5, 0, 0); // Start off-screen
      leftObject.scale.set(1, 1, 1);
      scene.add(leftObject);

      // --- RIGHT OBJECT ---
      rightObject = leftObject.clone(true);
      rightObject.position.set(5, 0, 0); // Start off-screen
      rightObject.scale.set(1, 1, 1);
      rightObject.rotation.y = Math.PI;
      scene.add(rightObject);

      // After cloning and positioning leftObject and rightObject:
      leftObject.traverse((child) => {
        if (child.type === 'Mesh') {
          child.material = new THREE.MeshStandardMaterial({
            map: leftTexture,
            emissive: new THREE.Color(0xffffff),
            emissiveMap: leftTexture,
            emissiveIntensity: 0.5,
            side: THREE.DoubleSide,
            metalness: 0.2,
            roughness: 0.5,
          });
          child.material.needsUpdate = true;
        }
      });
      rightObject.traverse((child) => {
        if (child.type === 'Mesh') {
          child.material = new THREE.MeshStandardMaterial({
            map: rightTexture,
            emissive: new THREE.Color(0xffffff),
            emissiveMap: rightTexture,
            emissiveIntensity: 0.5,
            side: THREE.DoubleSide,
            metalness: 0.2,
            roughness: 0.5,
          });
          child.material.needsUpdate = true;
        }
      });

      // Restore animation mixer logic
      if (gltf.animations && gltf.animations.length > 0) {
        mixerLeft = new THREE.AnimationMixer(leftObject);
        mixerRight = new THREE.AnimationMixer(rightObject);
        gltf.animations.forEach((clip) => {
          mixerLeft!.clipAction(clip).play();
          mixerRight!.clipAction(clip).play();
        });
      }
      animate();
    },
    undefined,
    (error) => {
      console.error('GLTFLoader error:', error);
    }
  );

  // Calculate off-screen positions based on display width
  const TRIANGLE_ON_X = 1.5; // On-screen X position (distance from center)
  const TRIANGLE_MARGIN = 100; // Extra px to ensure fully hidden
  const displayWidth = container.offsetWidth;
  const LEFT_X_OFF = window.innerWidth <= 700 ? TRIANGLE_ON_X * 8 : TRIANGLE_ON_X * 5; // scale to scene units
  const LEFT_X_ON = -TRIANGLE_ON_X;
  const RIGHT_X_OFF = window.innerWidth <= 700 ? -TRIANGLE_ON_X * 8 : -TRIANGLE_ON_X * 5; // scale to scene units
  const RIGHT_X_ON = TRIANGLE_ON_X;
  const TRIANGLE_SMOOTH = 0.08;
  let leftTargetX = LEFT_X_OFF;
  let rightTargetX = RIGHT_X_OFF;
  let leftCurrentX = LEFT_X_OFF;
  let rightCurrentX = RIGHT_X_OFF;

  // Animation loop
  const clock = new THREE.Clock();
  function animate() {
    requestAnimationFrame(animate);
    // On scroll end, smoothly decay currentSpeed to targetSpeed
    if (!isScrolling) {
      currentSpeed = currentSpeed * (1 - SPEED_DECAY) + targetSpeed * SPEED_DECAY;
      // Snap to target if very close
      if (Math.abs(currentSpeed - targetSpeed) < 0.01) currentSpeed = targetSpeed;
    }
    const baseDelta = clock.getDelta();
    if (mixerLeft) mixerLeft.update(baseDelta * currentSpeed);
    if (mixerRight) mixerRight.update(-1 * baseDelta * currentSpeed);
    // Camera Z position smoothing based on scroll
    // Get scroll progress from 0 to 1 for 0 to 100vh
    const scrollY = window.scrollY;
    const vh = window.innerHeight;
    // For triangle movement, use 0 to 150vh
    let triProgress = Math.min(Math.max((3 * scrollY) / vh, 0), 1);
    leftTargetX = LEFT_X_OFF + (LEFT_X_ON - LEFT_X_OFF) * triProgress;
    rightTargetX = RIGHT_X_OFF + (RIGHT_X_ON - RIGHT_X_OFF) * triProgress;
    leftCurrentX = leftCurrentX * (1 - TRIANGLE_SMOOTH) + leftTargetX * TRIANGLE_SMOOTH;
    rightCurrentX = rightCurrentX * (1 - TRIANGLE_SMOOTH) + rightTargetX * TRIANGLE_SMOOTH;
    if (leftObject) leftObject.position.x = leftCurrentX;
    if (rightObject) rightObject.position.x = rightCurrentX;
    // Camera Z position smoothing based on scroll (0 to 100vh)
    let camProgress = Math.min(Math.max((scrollY / vh) * 2, 0), 1);
    targetCameraZ = CAMERA_Z_START + (CAMERA_Z_END - CAMERA_Z_START) * camProgress;
    currentCameraZ = currentCameraZ * (1 - CAMERA_SMOOTH) + targetCameraZ * CAMERA_SMOOTH;
    camera.position.set(0, 0, currentCameraZ);
    // Rotate camera z if viewport width is 700px or less
    if (window.innerWidth <= 700) {
      camera.rotation.z = Math.PI / 2;
    } else {
      camera.rotation.z = 0;
    }
    renderer.render(scene, camera);
  }
};
